/**
 * Reward Helper
 * 辅助工具：简化在section中使用数据管理器
 */
(function() {
  'use strict';

  /**
   * Reward Helper类
   */
  class RewardHelper {
    constructor() {
      this.dataManager = window.RewardDataManager;
      this.apiService = window.RewardApiService;
    }

    /**
     * 初始化section的数据绑定
     * @param {HTMLElement} sectionElement - section的DOM元素
     * @param {object} config - 配置对象
     * @param {string} config.dataKey - 数据key（如 'points', 'discounts'）
     * @param {string} config.endpoint - API端点
     * @param {object} config.params - API参数
     * @param {function} config.onDataUpdate - 数据更新回调函数
     * @param {string|array} config.listenEvents - 监听的事件名称
     */
    initSection(sectionElement, config) {
      if (!this.dataManager) {
        console.error('RewardDataManager is not loaded');
        return;
      }

      const {
        dataKey,
        endpoint,
        params = {},
        onDataUpdate,
        listenEvents = null
      } = config;

      // 默认监听事件
      const defaultEvents = this.dataManager.EVENTS.DATA_REFRESHED;
      const eventsToListen = listenEvents || defaultEvents;
      const eventArray = Array.isArray(eventsToListen) ? eventsToListen : [eventsToListen];

      // 数据更新处理函数
      const handleDataUpdate = async (event) => {
        try {
          // 重新获取数据
          const data = await this.dataManager.getData(endpoint, params, true);
          
          // 调用更新回调
          if (onDataUpdate && typeof onDataUpdate === 'function') {
            onDataUpdate(data, sectionElement, event);
          }
        } catch (error) {
          console.error(`Error updating data for section ${dataKey}:`, error);
        }
      };

      // 监听事件
      eventArray.forEach(eventName => {
        window.addEventListener(eventName, handleDataUpdate);
      });

      // 初始加载数据
      this.loadSectionData(sectionElement, {
        endpoint,
        params,
        onDataUpdate
      });

      // 返回清理函数
      return () => {
        eventArray.forEach(eventName => {
          window.removeEventListener(eventName, handleDataUpdate);
        });
      };
    }

    /**
     * 加载section数据
     */
    async loadSectionData(sectionElement, config) {
      const {
        endpoint,
        params = {},
        onDataUpdate
      } = config;

      try {
        const data = await this.dataManager.getData(endpoint, params);
        
        if (onDataUpdate && typeof onDataUpdate === 'function') {
          onDataUpdate(data, sectionElement);
        }
      } catch (error) {
        console.error('Error loading section data:', error);
      }
    }

    /**
     * 更新积分显示（通用方法）
     * @param {HTMLElement} container - 容器元素
     * @param {number} points - 积分值
     * @param {object} options - 选项
     */
    updatePointsDisplay(container, points, options = {}) {
      const {
        selector = '[data-points-value]',
        format = (val) => val.toString(),
        animate = true
      } = options;

      const elements = container.querySelectorAll(selector);
      elements.forEach(element => {
        const formattedValue = format(points);
        
        if (animate) {
          // 简单的数字动画效果
          const currentValue = parseInt(element.textContent) || 0;
          this.animateNumber(element, currentValue, points, formattedValue, format);
        } else {
          element.textContent = formattedValue;
        }
      });
    }

    /**
     * 数字动画
     */
    animateNumber(element, from, to, finalText, format) {
      const duration = 500; // 动画时长（毫秒）
      const startTime = Date.now();
      
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // 使用easeOut缓动函数
        const easeOut = 1 - Math.pow(1 - progress, 3);
        const current = Math.round(from + (to - from) * easeOut);
        
        element.textContent = format(current);
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          element.textContent = finalText;
        }
      };
      
      animate();
    }

    /**
     * 提交操作（如兑换积分）
     * @param {string} endpoint - API端点
     * @param {object} data - 提交的数据
     * @param {object} options - 选项
     */
    async submitAction(endpoint, data, options = {}) {
      const {
        method = 'POST',
        onSuccess,
        onError,
        showLoading = true,
        loadingElement = null
      } = options;

      try {
        // 显示加载状态
        if (showLoading && loadingElement) {
          loadingElement.classList.add('loading');
          loadingElement.disabled = true;
        }

        const response = await this.dataManager.submitData(method, endpoint, data);

        // 成功回调
        if (onSuccess && typeof onSuccess === 'function') {
          onSuccess(response);
        }

        return response;
      } catch (error) {
        console.error('Submit action error:', error);
        
        // 错误回调
        if (onError && typeof onError === 'function') {
          onError(error);
        } else {
          // 默认错误处理
          alert(error.message || '操作失败，请稍后重试');
        }
        
        throw error;
      } finally {
        // 隐藏加载状态
        if (showLoading && loadingElement) {
          loadingElement.classList.remove('loading');
          loadingElement.disabled = false;
        }
      }
    }
  }

  // 导出单例实例
  window.RewardHelper = new RewardHelper();
})();

